import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Stack,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MenuRounded as MenuIcon,
  CloseRounded as CloseIcon,
  TravelExploreRounded as TripIcon,
  FlightTakeoff as FlightIcon,
  LocalOffer as DealsIcon,
  PersonRounded as PersonIcon,
  SettingsRounded as SettingsIcon,
  LogoutRounded as LogoutIcon,
  AddRounded as AddIcon,
  HelpOutline as HelpIcon,
  SmartToyRounded as AIIcon,
  Dashboard as DashboardIcon,
  Inventory as PackagesIcon,
  QuestionAnswer as InquiriesIcon,
  BookOnline as BookingsIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Groups as CommunityIcon,
  Description as DescriptionIcon,
  MenuBook as GuidesIcon,
  FmdGood as DestinationsIcon,
  Group as GroupIcon,
  CreditCard as BillingIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadMessagesContext } from '../contexts/UnreadMessagesContext';
import { useTranslation } from 'react-i18next';
import { isMobileApp } from '../utils/platformDetection';
import api from '../config/api';
import PartnerChrome from './PartnerChrome';
import { isPartnerLandingPath } from '../utils/appChrome';
import { isPartnerWorkspacePath, shouldUsePartnerChrome } from '../utils/partnerWorkspace';
import {
  DOCUMENT,
  DOCUMENT_NAV_ACTION_SPACING,
  DOCUMENT_NAV_LOGO_HEIGHT,
  DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
  documentChromeTabsSx,
  documentNavAuthCtaSx,
  documentNavAuthLinkSx,
  documentNavLogoSx,
} from '../constants/documentTheme';
import { brandedHomePath } from '../utils/brandedHomePath';
import { clearClientSessionBranding, isPartnerClientBrand, useClientBranding } from '../contexts/ClientBrandingContext';
import {
  memoriesUsesSeparateOrigin,
  performMainAppCrossLogout,
} from '../utils/memoriesAuthBridge';
import { clientBrandingAppliesPageTheme } from '../utils/partnerWorkspaceBrand';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import NotificationBell from './NotificationBell';
import NavLanguageSelector from './NavLanguageSelector';
import { PaidFeatureLabel } from './DocumentProBadge';
import TravelerMobileBottomNav from './TravelerMobileBottomNav';
import { NavMessageBadge, NavMessageCountChip } from './NavMessageBadge';
import useInquiryUnreadCount from '../hooks/useInquiryUnreadCount';
import {
  getPackagesListingPath,
  isPartnerCatalogPath,
} from '../utils/partnerCatalogStorage';

const mobileNavIconSx = {
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 2,
  width: 36,
  height: 36,
  flexShrink: 0,
  p: 0,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
};

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const { branding: clientBranding } = useClientBranding();
  const partnerClientBrand = !location.pathname.startsWith('/admin')
    && isPartnerClientBrand(clientBranding);
  const { refresh: refreshUnreadCounts } = useUnreadMessagesContext();
  const inquiryUnread = useInquiryUnreadCount({ enabled: Boolean(user), viewer: 'traveler' });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [workspacePartner, setWorkspacePartner] = React.useState(null);

  // Check if partner is logged in
  const partnerToken = localStorage.getItem('partnerToken');
  const storedPartner = partnerToken ? JSON.parse(localStorage.getItem('partner') || '{}') : null;
  const partner = workspacePartner || storedPartner;

  React.useEffect(() => {
    if (!partnerToken || !isPartnerWorkspacePath(location.pathname)) return undefined;
    let cancelled = false;
    api.get('/partners/profile').then((response) => {
      const next = response.data?.partner;
      if (cancelled || !next) return;
      localStorage.setItem('partner', JSON.stringify(next));
      setWorkspacePartner(next);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [partnerToken, location.pathname]);

  // Check if user is admin
  React.useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('token');
      if (!token || !user) {
        setIsAdmin(false);
        return;
      }
      try {
        const response = await api.get('/forum/admin/check');
        setIsAdmin(response.data.isAdmin);
      } catch (err) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [user]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    // Land on login (not marketing home) after a hard cross-origin clear.
    const afterLogout = '/login';
    handleClose();
    setMobileOpen(false);
    if (memoriesUsesSeparateOrigin()) {
      const returnTo = `${window.location.origin}${afterLogout}`;
      // Hard nav only — do not call logout() first (that races PrivateRoute
      // into flashing login/home before the Memories clear hop).
      await performMainAppCrossLogout(returnTo);
      return;
    }
    await logout();
    navigate(afterLogout);
  };
  
  const handlePartnerLogout = () => {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partner');
    clearClientSessionBranding();
    setWorkspacePartner(null);
    handleClose();
    setMobileOpen(false);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const packagesListingPath = getPackagesListingPath();
  const isPackagesNavActive =
    location.pathname === '/deals'
    || location.pathname.startsWith('/package')
    || isPartnerCatalogPath(location.pathname);
  
  // Check if we're on a trip details page
  const tripDetailsMatch = location.pathname.match(/^\/trips\/(\d+)/);
  const currentTripId = tripDetailsMatch ? tripDetailsMatch[1] : null;
  const isOnTripDetails = !!currentTripId;
  const tripMemoriesPath = currentTripId ? `/trips/${currentTripId}?tab=memories` : '/memories';
  const isOnTripMemoriesTab = Boolean(
    isOnTripDetails && new URLSearchParams(location.search).get('tab') === 'memories'
  );
  const isMemoriesNavActive = location.pathname === '/memories'
    || location.pathname.startsWith('/memories/')
    || isOnTripMemoriesTab;

  const handleTripsClick = (event) => {
    console.log('🔄 [Navbar] Trips clicked - refreshing unread counts');
    refreshUnreadCounts();
    // Let the RouterLink handle the navigation
  };

  const handleLogoClick = () => {
    console.log('🔄 [Navbar] Logo clicked - refreshing unread counts');
    refreshUnreadCounts();
  };

  const showAdminNav = Boolean(user && isAdmin && !partner);

  const getTabValue = () => {
    if (user) {
      // String values so conditional Admin/AI tabs do not shift indices.
      if (showAdminNav && location.pathname.startsWith('/admin')) return 'admin';
      if (isMemoriesNavActive) return 'memories';
      if (location.pathname === '/trips' || isOnTripDetails) return 'trips';
      if (location.pathname === '/advisors' || location.pathname.startsWith('/advisors/')) return 'advisors';
      if (location.pathname === '/deals' || location.pathname.startsWith('/package') || isPartnerCatalogPath(location.pathname)) return 'packages';
      if (location.pathname === '/guides' || location.pathname.startsWith('/guides/')) return 'guides';
      if (location.pathname === '/destinations' || location.pathname.startsWith('/destinations/')) return 'destinations';
      if (location.pathname === '/forum' || location.pathname.startsWith('/forum/')) return 'forum';
      if (location.pathname === '/my-inquiries' || location.pathname.startsWith('/my-inquiries/')) return 'requests';
      if (location.pathname === '/my-bookings' || location.pathname.startsWith('/my-bookings/')) return 'bookings';
      if (location.pathname === '/ai-concierge') return 'ai';
    } else {
      // Order: plan, advisors, packages, guides, destinations, community
      if (location.pathname === '/trip-planner') return 0;
      if (location.pathname === '/advisors' || location.pathname.startsWith('/advisors/')) return 1;
      if (location.pathname === '/deals' || location.pathname.startsWith('/package') || isPartnerCatalogPath(location.pathname)) return 2;
      if (location.pathname === '/guides' || location.pathname.startsWith('/guides/')) return 3;
      if (location.pathname === '/destinations' || location.pathname.startsWith('/destinations/')) return 4;
      if (location.pathname === '/forum' || location.pathname.startsWith('/forum/')) return 5;
    }
    return false;
  };

  const onPartnerLanding = isPartnerLandingPath(location.pathname);

  if (shouldUsePartnerChrome(location.pathname, partner)) {
    return <PartnerChrome partner={partner} onLogout={handlePartnerLogout} />;
  }

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        className="professional-navbar"
        sx={{ 
          backgroundColor: partnerClientBrand
            && !clientBrandingAppliesPageTheme(clientBranding)
            && clientBranding.color
            ? clientBranding.color
            : DOCUMENT.nav,
          backgroundImage: 'none',
          backdropFilter: 'none',
          borderBottom: 'none',
          boxShadow: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              py: 0.5,
              minHeight: { xs: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE + 8, md: DOCUMENT_NAV_LOGO_HEIGHT },
              overflow: 'hidden',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexShrink: 1 }}>
              <Box
                component={RouterLink}
                to={brandedHomePath(clientBranding, Boolean(user)) || '/'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
                onClick={handleLogoClick}
              >
                {partnerClientBrand && clientBranding.logoUrl ? (
                  <Box
                    component="img"
                    src={clientBranding.logoUrl}
                    alt={clientBranding.name}
                    sx={{
                      ...documentNavLogoSx,
                      height: { xs: 40, md: 52 },
                      maxWidth: 200,
                      objectFit: 'contain',
                      bgcolor: 'transparent',
                      p: 0,
                      my: 0.75,
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src="/noBgWhite.png"
                    alt="Déjà Vu Nomad"
                    sx={{
                      ...documentNavLogoSx,
                      height: { xs: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE, md: DOCUMENT_NAV_LOGO_HEIGHT },
                      maxWidth: { xs: 148, md: 'none' },
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: DOCUMENT_NAV_ACTION_SPACING }, flexShrink: 0 }}>
              <NavLanguageSelector />
              {user || partner ? (
                <>
                  {/* Partners have their own workspace alerts; this feed is the
                      traveller's, so it only shows for signed-in users. */}
                  <NotificationBell enabled={Boolean(user) && !partner} />

                  <IconButton
                    aria-label={t('nav.menu')}
                    sx={mobileNavIconSx}
                    onClick={handleMobileToggle}
                  >
                    <MenuIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    onClick={handleMenu}
                    sx={{
                      p: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Avatar 
                      src={
                        isUsableImageUrl(partner?.avatar)
                          ? partner.avatar
                          : isUsableImageUrl(user?.avatar)
                            ? user.avatar
                            : undefined
                      } 
                      sx={{ 
                        width: 40, 
                        height: 40,
                        fontWeight: 600,
                        backgroundColor: partner ? '#764ba2' : '#591716',
                        color: 'white',
                        fontSize: '1.1rem',
                      }}
                    >
                      {partner ? (partner.company_name?.charAt(0).toUpperCase() || 'P') : (user?.username ? user.username.charAt(0).toUpperCase() : 'U')}
                    </Avatar>
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    sx={{
                      mt: 1,
                      '& .MuiPaper-root': {
                        backgroundColor: 'rgba(17, 17, 17, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 3,
                        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.3)',
                        minWidth: 200,
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                        }}
                      >
                        {partner ? partner.company_name : (user?.username || 'User')}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.85rem',
                        }}
                      >
                        {partner ? partner.email : user?.email}
                      </Typography>
                      {isAdmin && !partner && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#ff4444',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Admin
                        </Typography>
                      )}
                    </Box>
                    
                    {partner ? (
                      <>
                        {/* Partner Info Details - only show if any data exists */}
                        {(partner.contact_name || partner.contactName || partner.phone_number || partner.company_address || partner.business_registration_number) && (
                          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            {(partner.contact_name || partner.contactName) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PersonIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }} />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  {partner.contact_name || partner.contactName}
                                </Typography>
                              </Box>
                            )}
                            {partner.phone_number && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PhoneIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }} />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  {partner.phone_number}
                                </Typography>
                              </Box>
                            )}
                            {(partner.company_address || partner.address) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocationIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }} />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  {partner.company_address || partner.address}
                                </Typography>
                              </Box>
                            )}
                            {(partner.business_registration_number || partner.registration_number) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }} />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  {partner.business_registration_number || partner.registration_number}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        
                        {/* Partner - Profile actions */}
                        <MenuItem 
                          onClick={() => { navigate('/partner/billing'); handleClose(); }}
                          sx={{
                            color: 'white',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <BillingIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={t('nav.partnerBilling')} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </MenuItem>
                        <MenuItem 
                          onClick={() => { navigate('/partner/dashboard'); handleClose(); }}
                          sx={{
                            color: 'white',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <EditIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={t('nav.editProfile')} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem 
                          onClick={() => { navigate('/profile'); handleClose(); }}
                          sx={{
                            color: 'white',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <PersonIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={t('nav.profile')} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </MenuItem>
                        
                        <MenuItem 
                          onClick={() => { navigate('/preferences'); handleClose(); }}
                          sx={{
                            color: 'white',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <SettingsIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={t('nav.preferences')} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </MenuItem>
                      </>
                    )}
                    
                    {/* Admin — only for is_admin users (same gate as desktop Admin tab) */}
                    {showAdminNav && (
                      <MenuItem
                        onClick={() => {
                          navigate('/admin');
                          handleClose();
                        }}
                        data-testid="nav-admin-menu-item"
                        sx={{
                          color: 'white',
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <DashboardIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('nav.admin')}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      </MenuItem>
                    )}
                    
                    {!partner && (
                      <MenuItem
                        onClick={() => {
                          navigate('/how-to');
                          handleClose();
                        }}
                        sx={{
                          color: 'white',
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <HelpIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('howTo.menu')}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </MenuItem>
                    )}

                    {!partner && (
                      <MenuItem 
                        onClick={() => { 
                          navigate('/support');
                          handleClose(); 
                        }}
                        sx={{
                          color: 'white',
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <HelpIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('nav.support')} 
                          primaryTypographyProps={{ 
                            fontSize: '0.9rem',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </MenuItem>
                    )}
                    
                    {/* Terms of Service - for users */}
                    {!partner && (
                      <MenuItem 
                        onClick={() => { 
                          navigate('/terms');
                          handleClose(); 
                        }}
                        sx={{
                          color: 'white',
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <DescriptionIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Terms of Service" 
                          primaryTypographyProps={{ 
                            fontSize: '0.9rem',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </MenuItem>
                    )}
                    
                    {/* Partner Agreement - for partners only */}
                    {partner && (
                      <MenuItem 
                        onClick={() => { 
                          navigate('/partner-terms');
                          handleClose(); 
                        }}
                        sx={{
                          color: 'white',
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <DescriptionIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Partner Agreement" 
                          primaryTypographyProps={{ 
                            fontSize: '0.9rem',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </MenuItem>
                    )}
                    
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    
                    <MenuItem 
                      onClick={partner ? handlePartnerLogout : handleLogout}
                      sx={{
                        color: '#ff6b6b',
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        }
                      }}
                    >
                      <ListItemIcon>
                        <LogoutIcon sx={{ color: '#ff6b6b', fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('nav.logout')} 
                        primaryTypographyProps={{ 
                          fontSize: '0.9rem',
                          color: '#ff6b6b',
                          fontWeight: 500
                        }}
                      />
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  {/* Desktop: Show all buttons */}
                  <Stack direction="row" spacing={DOCUMENT_NAV_ACTION_SPACING} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/login"
                      sx={documentNavAuthLinkSx}
                    >
                      {t('nav.signIn')}
                    </Button>
                    {!onPartnerLanding && (
                      <Button
                        component={RouterLink}
                        to="/partner"
                        data-testid="nav-professionals-cta"
                        sx={documentNavAuthCtaSx}
                      >
                        {t('nav.partnerSignup')}
                      </Button>
                    )}
                  </Stack>
                  
                  {/* Mobile: user icon + Partner + hamburger */}
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      component={RouterLink}
                      to="/login"
                      aria-label={t('nav.signIn')}
                      sx={mobileNavIconSx}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    {!onPartnerLanding && (
                      <Button
                        component={RouterLink}
                        to="/partner"
                        aria-label={t('nav.partnerSignup')}
                        data-testid="nav-professionals-cta-mobile"
                        sx={{
                          ...documentNavAuthCtaSx,
                          px: 1,
                          fontSize: 10,
                          letterSpacing: '0.06em',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {t('nav.partnerSignupShort')}
                      </Button>
                    )}
                    <IconButton
                      aria-label={t('nav.menu')}
                      sx={mobileNavIconSx}
                      onClick={handleMobileToggle}
                    >
                      <MenuIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
        
        {!loading && (user || (!user && !partner)) && (
          <Box
            data-testid={user ? 'signed-in-nav-tabs' : 'public-nav-tabs'}
            sx={{
            display: { xs: 'none', md: 'block' },
            backgroundColor: DOCUMENT.nav,
            borderTop: `1px solid ${DOCUMENT.navRule}`,
            borderBottom: `1px solid ${DOCUMENT.navRule}`,
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}>
            <Container maxWidth="xl">
              <Tabs
                value={getTabValue()}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={documentChromeTabsSx}
              >
                {user ? (
                  <Tab
                    value="trips"
                    label={t('nav.myTrips')}
                    component={RouterLink}
                    to="/trips"
                    onClick={handleTripsClick}
                  />
                ) : null}
                {user && (
                  <Tab
                    value="memories"
                    label={t('nav.memories')}
                    component={RouterLink}
                    to={tripMemoriesPath}
                  />
                )}
                {!user && (
                  <Tab
                    label={t('nav.planATrip')}
                    component={RouterLink}
                    to="/trip-planner"
                  />
                )}
                <Tab
                  value={user ? 'advisors' : undefined}
                  label={t('nav.advisors')}
                  component={RouterLink}
                  to="/advisors"
                />
                <Tab
                  value={user ? 'packages' : undefined}
                  label={t('nav.packages')}
                  component={RouterLink}
                  to={packagesListingPath}
                />
                <Tab
                  value={user ? 'guides' : undefined}
                  label={t('nav.tabGuides')}
                  component={RouterLink}
                  to="/guides"
                />
                <Tab
                  value={user ? 'destinations' : undefined}
                  label={t('nav.destinations')}
                  component={RouterLink}
                  to="/destinations"
                />
                <Tab
                  value={user ? 'forum' : undefined}
                  label={t('nav.forum')}
                  component={RouterLink}
                  to="/forum"
                />
                {user && (
                  <Tab
                    value="requests"
                    label={(
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        {t('nav.requests')}
                        <NavMessageCountChip count={inquiryUnread} />
                      </Box>
                    )}
                    component={RouterLink}
                    to="/my-inquiries"
                  />
                )}
                {user && (
                  <Tab
                    value="bookings"
                    label={t('nav.myBookings')}
                    component={RouterLink}
                    to="/my-bookings"
                  />
                )}
                {user && !isMobileApp() && !partnerClientBrand && (
                  <Tab
                    value="ai"
                    label={(
                      <PaidFeatureLabel variant="icon">
                        {t('nav.aiConcierge')}
                      </PaidFeatureLabel>
                    )}
                    component={RouterLink}
                    to="/ai-concierge"
                  />
                )}
                {showAdminNav && (
                  <Tab
                    value="admin"
                    label={t('nav.admin')}
                    component={RouterLink}
                    to="/admin"
                    data-testid="nav-admin-tab"
                  />
                )}
              </Tabs>
            </Container>
          </Box>
        )}
      </AppBar>

      {!loading && (user || (!user && !partner)) && !onPartnerLanding && (
        <TravelerMobileBottomNav signedIn={Boolean(user)} />
      )}

      {/* Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleMobileToggle}
        disableScrollLock={onPartnerLanding}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            maxWidth: '100%',
            backgroundColor: 'rgba(17, 17, 17, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRight: 'none',
          },
        }}
      >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <img
                src="/symbol.svg"
                alt="Déjà Vu Nomad"
                style={{
                  height: '40px',
                  width: 'auto',
                  borderRadius: '8px',
                }}
              />
              <IconButton 
                onClick={handleMobileToggle}
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <List sx={{ pt: 0 }}>
              {!user && !partner ? (
                <>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/trip-planner"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/trip-planner') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <TripIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.planATrip')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/trip-planner') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/advisors"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/advisors') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.advisors')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/advisors') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to={packagesListingPath}
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isPackagesNavActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DealsIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.packages')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isPackagesNavActive ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/guides"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/guides') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GuidesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.tabGuides')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/guides') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/destinations"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/destinations') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DestinationsIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.destinations')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/destinations') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/forum"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/forum') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <CommunityIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.forum')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/forum') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                  <ListItem
                    button
                    component={RouterLink}
                    to="/login"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <PersonIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.signIn')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  {!onPartnerLanding && (
                  <ListItem
                    button
                    component={RouterLink}
                    to="/partner"
                    data-testid="nav-professionals-drawer"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DashboardIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.partnerSignup')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  )}
                </>
              ) : !partner && (
                <>
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/trips"
                    onClick={() => { handleTripsClick(); handleMobileToggle(); }}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/trips') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <TripIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.myTrips')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/trips') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to={tripMemoriesPath}
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isMemoriesNavActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GuidesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.memories')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isMemoriesNavActive ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/advisors"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/advisors') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.advisors')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/advisors') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to={packagesListingPath}
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isPackagesNavActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DealsIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.packages')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isPackagesNavActive ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/guides"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/guides') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GuidesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.guides')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/guides') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/destinations"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/destinations') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DestinationsIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.destinations')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/destinations') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/forum"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/forum') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <CommunityIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.forum')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/forum') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/my-inquiries"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: location.pathname.startsWith('/my-inquiries') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <NavMessageBadge count={inquiryUnread}>
                        <InquiriesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                      </NavMessageBadge>
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.requests')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: location.pathname.startsWith('/my-inquiries') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem
                    button
                    component={RouterLink}
                    to="/my-bookings"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/my-bookings') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <FlightIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.myBookings')}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isActive('/my-bookings') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  
                  {!isMobileApp() && !partnerClientBrand && (
                    <ListItem 
                      button 
                      component={RouterLink} 
                      to="/ai-concierge"
                      onClick={handleMobileToggle}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: isActive('/ai-concierge') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AIIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                      </ListItemIcon>
                    <ListItemText 
                      primary={(
                        <PaidFeatureLabel variant="icon">
                          {t('nav.aiConcierge')}
                        </PaidFeatureLabel>
                      )}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/ai-concierge') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                    </ListItem>
                  )}

                  {showAdminNav && (
                    <ListItem
                      button
                      component={RouterLink}
                      to="/admin"
                      onClick={handleMobileToggle}
                      data-testid="nav-admin-drawer-item"
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: location.pathname.startsWith('/admin') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <DashboardIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('nav.admin')}
                        primaryTypographyProps={{
                          color: 'white',
                          fontWeight: location.pathname.startsWith('/admin') ? 600 : 500,
                          fontSize: '0.95rem',
                        }}
                      />
                    </ListItem>
                  )}
                  
                  {/* Trip Sub-Tabs - Show when on trip details page */}
                  {isOnTripDetails && (
                    <>
                      <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', px: 2, mb: 1, display: 'block' }}>
                        Trip Sections
                      </Typography>
                      {[
                        { label: t('tripTabs.itinerary'), hash: '' },
                        { label: t('tripTabs.memories'), hash: 'memories' },
                        { label: t('tripTabs.explore'), hash: 'explore' },
                        { label: t('tripTabs.members'), hash: 'members' },
                        { label: t('tripTabs.chat'), hash: 'chat' },
                        { label: t('tripTabs.map'), hash: 'map' },
                        { label: t('tripTabs.checklist'), hash: 'checklist' },
                        { label: t('tripTabs.expenses'), hash: 'expenses' },
                        { label: t('tripTabs.documents'), hash: 'documents' },
                      ].map((tab) => (
                        <ListItem
                          key={tab.label}
                          button
                          onClick={() => {
                            const url = tab.hash
                              ? `/trips/${currentTripId}?tab=${tab.hash}`
                              : `/trips/${currentTripId}`;
                            navigate(url);
                            handleMobileToggle();
                          }}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            py: 1,
                            pl: 3,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ListItemText 
                            primary={tab.label}
                            primaryTypographyProps={{ 
                              color: 'rgba(255,255,255,0.8)',
                              fontSize: '0.85rem',
                            }}
                          />
                        </ListItem>
                      ))}
                    </>
                  )}
                  
                  <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/trips/create"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 2,
                      backgroundColor: '#591716',
                      '&:hover': {
                        backgroundColor: '#7a1f1e',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <AddIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('trip.createTrip')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                </>
              )}
              
              {partner && (
                <>
                  {/* Navigation Items */}
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/dashboard"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/dashboard') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <DashboardIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerDashboard')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/dashboard') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/trips"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/trips') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <TripIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerTrips')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/trips') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/master-trips"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/master-trips') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Group Trips"
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/master-trips') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/packages"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/packages') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <PackagesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerPackages')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/packages') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/inquiries"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/inquiries') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <InquiriesIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerInquiries')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/inquiries') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/bookings"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/bookings') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <BookingsIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerBookings')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/bookings') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>

                  <ListItem 
                    button 
                    component={RouterLink} 
                    to="/partner/billing"
                    onClick={handleMobileToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: isActive('/partner/billing') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <BillingIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('nav.partnerBilling')}
                      primaryTypographyProps={{ 
                        color: 'white',
                        fontWeight: isActive('/partner/billing') ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItem>
                  
                  </>
              )}
              
            </List>
          </Box>
        </Drawer>
    </>
  );
};

export default Navbar; 